import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

import MesoVolumeConfigDisplay from 'components/Program/MesoVolumeConfig/MesoVolumeConfigDisplay'

const INITIAL_MOUNT_Y_OFFSET = 25
const MESO_VOLUME_CARD_GAP = 48
const MESO_VOLUME_CARD_LABEL_HEIGHT = 20
const MESO_VOLUME_CARD_LABEL_GAP = 8
// Used to center the board before each card reports its content height.
const MESO_VOLUME_CONFIG_DISPLAY_FALLBACK_HEIGHT = 900
// p-4 on the edit display.
const MESO_VOLUME_DISPLAY_PADDING_X = 32
// gap-x-4 between the add buttons and week columns.
const MESO_VOLUME_DISPLAY_GAP = 16
// CreateMicroVolumeConfigButton edit variant is w-20.
const CREATE_MICRO_VOLUME_BUTTON_WIDTH = 80
// MicroVolumeConfigBar edit variant is w-80.
const MICRO_VOLUME_COLUMN_WIDTH = 320
const MESO_VOLUME_DISPLAY_WIDTH_SLACK = 32
const EMPTY_MESO_VOLUME_CONFIG_DISPLAY_WIDTH = 480
const EMPTY_BOARD_WIDTH = 320
const EMPTY_BOARD_HEIGHT = 96
const MESO_VOLUME_LEVELS = [1, 2, 3, 4, 5, 6]

function sameId(left, right) {
    return String(left) === String(right)
}

function sortBySortOrder(items = []) {
    return [...items].sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
}

function estimateMesoVolumeConfigDisplayWidth(mesoVolumeConfig) {
    const microCount = mesoVolumeConfig?.microVolumes?.length ?? 0

    if (microCount === 0) {
        return EMPTY_MESO_VOLUME_CONFIG_DISPLAY_WIDTH
    }

    // Centered scroll in the edit display hides the side spacers, so the
    // card needs to fit both add buttons and every week column.
    const weekColumnsWidth = microCount * MICRO_VOLUME_COLUMN_WIDTH
    const weekColumnGaps = Math.max(0, microCount - 1) * MESO_VOLUME_DISPLAY_GAP
    const addButtonsWidth = CREATE_MICRO_VOLUME_BUTTON_WIDTH * 2
    const addButtonGaps = MESO_VOLUME_DISPLAY_GAP * 2

    return MESO_VOLUME_DISPLAY_PADDING_X
        + addButtonsWidth
        + addButtonGaps
        + weekColumnsWidth
        + weekColumnGaps
        + MESO_VOLUME_DISPLAY_WIDTH_SLACK
}

function buildMesoVolumeCanvasLayout(mesoVolumeConfigs) {
    const layouts = []
    let offsetX = 0

    mesoVolumeConfigs.forEach((mesoVolumeConfig) => {
        const width = estimateMesoVolumeConfigDisplayWidth(mesoVolumeConfig)

        layouts.push({
            mesoVolumeConfig,
            x: offsetX,
            y: 0,
            width,
        })

        offsetX += width + MESO_VOLUME_CARD_GAP
    })

    return {
        layouts,
        boardWidth: Math.max(0, offsetX - MESO_VOLUME_CARD_GAP),
    }
}

function CanvasMetadataDropdown({
    items = [],
    idKey,
    selectedId,
    onChange,
    placeholder,
}) {
    const selectedItem = items.find((item) => sameId(item[idKey], selectedId))

    return (
        <Listbox
            value={selectedId}
            onChange={onChange}
        >
            <div className="relative w-full">
                <Listbox.Button className="relative flex h-9 w-full cursor-pointer items-center rounded-md border border-zinc-200 bg-white/90 pl-3 pr-9 text-left text-sm text-zinc-700 shadow-sm backdrop-blur hover:bg-white focus:outline-none focus:ring-2 focus:ring-orange-400">
                    <span className="flex min-w-0 items-center gap-x-2">
                        <span
                            className="h-3 w-3 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: selectedItem?.colorHex ?? '#d4d4d8' }}
                        />
                        <span className="block truncate">
                            {selectedItem?.name ?? placeholder}
                        </span>
                    </span>

                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-zinc-400" />
                    </span>
                </Listbox.Button>

                <Transition
                    as="div"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-zinc-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                        {items.map((item) => (
                            <Listbox.Option
                                key={item[idKey]}
                                value={item[idKey]}
                                className={({ active }) => `
                                    relative cursor-pointer select-none py-2 pl-3 pr-9
                                    ${active ? 'bg-orange-50' : ''}
                                `}
                            >
                                {({ selected }) => (
                                    <>
                                        <span className="flex items-center gap-x-2">
                                            <span
                                                className="h-3 w-3 flex-shrink-0 rounded-full"
                                                style={{ backgroundColor: item.colorHex ?? '#d4d4d8' }}
                                            />
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                {item.name}
                                            </span>
                                        </span>

                                        {selected &&
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-orange-600">
                                                <CheckIcon className="h-5 w-5" />
                                            </span>
                                        }
                                    </>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    )
}

function MesoVolumeLevelDropdown({
    selectedLevel,
    onChange,
}) {
    return (
        <Listbox
            value={selectedLevel}
            onChange={onChange}
        >
            <div className="relative w-full">
                <Listbox.Button className="relative flex h-9 w-full cursor-pointer items-center rounded-md border border-zinc-200 bg-white/90 pl-3 pr-8 text-left text-sm text-zinc-700 shadow-sm backdrop-blur hover:bg-white focus:outline-none focus:ring-2 focus:ring-orange-400">
                    <span className="block truncate tabular-nums">
                        {selectedLevel}
                    </span>

                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                        <ChevronUpDownIcon className="h-5 w-5 text-zinc-400" />
                    </span>
                </Listbox.Button>

                <Transition
                    as="div"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-zinc-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                        {MESO_VOLUME_LEVELS.map((level) => (
                            <Listbox.Option
                                key={level}
                                value={level}
                                className={({ active }) => `
                                    relative cursor-pointer select-none py-2 pl-3 pr-8
                                    ${active ? 'bg-orange-50' : ''}
                                `}
                            >
                                {({ selected }) => (
                                    <>
                                        <span className={`block truncate tabular-nums ${selected ? 'font-semibold' : 'font-normal'}`}>
                                            {level}
                                        </span>

                                        {selected &&
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-orange-600">
                                                <CheckIcon className="h-5 w-5" />
                                            </span>
                                        }
                                    </>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    )
}

function MesoVolumeCanvasCard({
    mesoVolumeId,
    x,
    y,
    width,
    onCardHeight,
    children,
}) {
    const cardRef = useRef(null)

    useLayoutEffect(() => {
        const element = cardRef.current
        if (!element) return

        const ReportHeight = () => {
            onCardHeight(mesoVolumeId, Math.ceil(element.offsetHeight))
        }

        ReportHeight()

        const resizeObserver = new ResizeObserver(ReportHeight)
        resizeObserver.observe(element)

        return () => resizeObserver.disconnect()
    }, [mesoVolumeId, onCardHeight])

    return (
        <div
            ref={cardRef}
            className="absolute"
            style={{
                left: x,
                top: y,
                width,
            }}
            onPointerDown={(event) => event.stopPropagation()}
        >
            {children}
        </div>
    )
}

export default function MesoVolumeCanvasTab({
    viewportRef,
    setTransform = () => {},
    programMetadata = {},
    mesoVolumeConfigs = [],
}) {
    const macroVolumeFocusTypes = useMemo(
        () => sortBySortOrder(programMetadata?.macroVolumeFocusTypes),
        [programMetadata],
    )
    const programLevels = useMemo(
        () => sortBySortOrder(programMetadata?.programLevels),
        [programMetadata],
    )

    const [selectedMacroVolumeFocusTypeId, setSelectedMacroVolumeFocusTypeId] = useState(
        () => macroVolumeFocusTypes[0]?.macroVolumeFocusTypeId ?? null,
    )
    const [selectedProgramLevelId, setSelectedProgramLevelId] = useState(
        () => programLevels[0]?.programLevelId ?? null,
    )
    const [selectedMesoVolumeLevel, setSelectedMesoVolumeLevel] = useState(MESO_VOLUME_LEVELS[0])
    const [isCanvasReady, setIsCanvasReady] = useState(false)
    const [portalElement, setPortalElement] = useState(null)

    useEffect(() => {
        const hasSelectedFocusType = macroVolumeFocusTypes.some((focusType) => (
            sameId(focusType.macroVolumeFocusTypeId, selectedMacroVolumeFocusTypeId)
        ))

        if (!hasSelectedFocusType) {
            setSelectedMacroVolumeFocusTypeId(macroVolumeFocusTypes[0]?.macroVolumeFocusTypeId ?? null)
        }
    }, [macroVolumeFocusTypes, selectedMacroVolumeFocusTypeId])

    useEffect(() => {
        const hasSelectedProgramLevel = programLevels.some((programLevel) => (
            sameId(programLevel.programLevelId, selectedProgramLevelId)
        ))

        if (!hasSelectedProgramLevel) {
            setSelectedProgramLevelId(programLevels[0]?.programLevelId ?? null)
        }
    }, [programLevels, selectedProgramLevelId])

    const visibleMesoVolumeConfigs = useMemo(() => (
        mesoVolumeConfigs.filter((mesoVolumeConfig) => (
            sameId(mesoVolumeConfig.macroVolumeFocusTypeId, selectedMacroVolumeFocusTypeId)
            && sameId(mesoVolumeConfig.programLevelId, selectedProgramLevelId)
            && Number(mesoVolumeConfig.level) === selectedMesoVolumeLevel
        ))
    ), [mesoVolumeConfigs, selectedMacroVolumeFocusTypeId, selectedProgramLevelId, selectedMesoVolumeLevel])

    const volumeLayout = useMemo(() => {
        if (visibleMesoVolumeConfigs.length === 0) {
            return {
                layouts: [],
                boardWidth: EMPTY_BOARD_WIDTH,
                boardHeight: EMPTY_BOARD_HEIGHT,
            }
        }

        return buildMesoVolumeCanvasLayout(visibleMesoVolumeConfigs)
    }, [visibleMesoVolumeConfigs])

    const { layouts, boardWidth } = volumeLayout
    const [cardHeights, setCardHeights] = useState({})

    const onCardHeight = useCallback((mesoVolumeId, height) => {
        setCardHeights((current) => {
            if (Math.abs((current[mesoVolumeId] ?? 0) - height) <= 1) return current
            return { ...current, [mesoVolumeId]: height }
        })
    }, [])

    const measuredCardHeights = layouts
        .map((layout) => cardHeights[layout.mesoVolumeConfig.mesoVolumeId])
        .filter((height) => height > 0)

    const boardHeight = layouts.length === 0
        ? EMPTY_BOARD_HEIGHT
        : measuredCardHeights.length > 0
            ? Math.max(...measuredCardHeights)
            : MESO_VOLUME_CARD_LABEL_HEIGHT + MESO_VOLUME_CARD_LABEL_GAP + MESO_VOLUME_CONFIG_DISPLAY_FALLBACK_HEIGHT

    useLayoutEffect(() => {
        setPortalElement(viewportRef?.current ?? null)
    }, [viewportRef])

    useLayoutEffect(() => {
        const viewport = viewportRef?.current
        if (!viewport) return

        const { width, height } = viewport.getBoundingClientRect()

        setTransform({
            x: Math.max(0, width / 2 - boardWidth / 2),
            y: Math.max(0, height / 2 - boardHeight / 2 + INITIAL_MOUNT_Y_OFFSET),
            zoom: 1,
        })

        setIsCanvasReady(true)
    }, [boardWidth, boardHeight, selectedMacroVolumeFocusTypeId, selectedProgramLevelId, selectedMesoVolumeLevel, viewportRef, setTransform])

    useLayoutEffect(() => {
        return () => setIsCanvasReady(false)
    }, [])

    const filterDropdowns = portalElement && createPortal(
        <div
            className="absolute top-[4.5rem] left-1/2 z-50 flex -translate-x-1/2 items-center gap-2"
            onPointerDown={(event) => event.stopPropagation()}
            onWheel={(event) => event.stopPropagation()}
        >
            {macroVolumeFocusTypes.length > 0 &&
                <div className="w-56">
                    <CanvasMetadataDropdown
                        items={macroVolumeFocusTypes}
                        idKey="macroVolumeFocusTypeId"
                        selectedId={selectedMacroVolumeFocusTypeId}
                        onChange={setSelectedMacroVolumeFocusTypeId}
                        placeholder="Select focus"
                    />
                </div>
            }

            {programLevels.length > 0 &&
                <div className="w-56">
                    <CanvasMetadataDropdown
                        items={programLevels}
                        idKey="programLevelId"
                        selectedId={selectedProgramLevelId}
                        onChange={setSelectedProgramLevelId}
                        placeholder="Select level"
                    />
                </div>
            }

            <div className="w-20">
                <MesoVolumeLevelDropdown
                    selectedLevel={selectedMesoVolumeLevel}
                    onChange={setSelectedMesoVolumeLevel}
                />
            </div>
        </div>,
        portalElement,
    )

    return (
        <>
            {filterDropdowns}

            {isCanvasReady &&
                <div className="absolute left-0 top-0">
                    {layouts.length === 0 &&
                        <div
                            className="flex items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-white text-sm text-zinc-400"
                            style={{
                                width: EMPTY_BOARD_WIDTH,
                                height: EMPTY_BOARD_HEIGHT,
                            }}
                        >
                            No meso volumes match these filters
                        </div>
                    }

                    {layouts.map(({ mesoVolumeConfig, x, y, width }) => (
                        <MesoVolumeCanvasCard
                            key={mesoVolumeConfig.mesoVolumeId}
                            mesoVolumeId={mesoVolumeConfig.mesoVolumeId}
                            x={x}
                            y={y}
                            width={width}
                            onCardHeight={onCardHeight}
                        >
                            <div className="flex flex-col gap-2">
                                <p
                                    className="h-5 shrink-0 truncate text-left text-sm font-medium text-gray-900"
                                    title={mesoVolumeConfig.name}
                                >
                                    {mesoVolumeConfig.name}
                                </p>

                                <MesoVolumeConfigDisplay
                                    mode="edit"
                                    enableAnimation={false}
                                    editHeight="auto"
                                    parameterMacroVolumeFocusTypeHashMap={programMetadata.macroVolumeFocusTypeHashMap}
                                    parameterMicroVolumeStimulusTypeHashMap={programMetadata.microVolumeStimulusTypeHashMap}
                                    mesoVolumeConfig={mesoVolumeConfig}
                                />
                            </div>
                        </MesoVolumeCanvasCard>
                    ))}
                </div>
            }
        </>
    )
}
